import { Component } from '@theme/component';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/** Focusable elements the drawer moves focus to on open (matches #focusFirst). */
const FOCUSABLE_SELECTOR = "a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])";

/**
 * A custom element that manages the main menu drawer.
 *
 * B3 / Approach 4 — the modal decision. The top-level container is a <dialog> (converted from
 * <details>) and opens MODAL on every breakpoint via showModal(), so the browser provides the
 * focus trap + inert background + the ::backdrop natively — no manual inert walk, no custom
 * backdrop element. In THIS store the header nav is inline on desktop (menu_style: 'menu'), so
 * the hamburger is mobile-only and modal-everywhere is correct. If menu_style is ever switched
 * to 'drawer', the hamburger becomes a content-height DESKTOP dropdown, where a modal focus trap
 * would be odd — the fix then is the mobile-modal / desktop-non-modal split (see CLAUDE.md).
 *
 * The nested submenus stay as <details> and open/close exactly as before.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} details - The dialog container (ref name kept as `details`).
 * @property {HTMLDivElement} menuDrawer - The sliding drawer panel.
 * @property {HTMLButtonElement} trigger - The hamburger button that opens the drawer.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer', 'trigger'];

  connectedCallback() {
    super.connectedCallback();
    // Escape on a modal dialog fires `cancel`; run our animated close instead of the instant
    // native one (see #onCancel).
    this.refs.details.addEventListener('cancel', this.#onCancel);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.details.removeEventListener('cancel', this.#onCancel);
  }

  /**
   * Escape → animated close. preventDefault suppresses the dialog's instant native close so the
   * .menu-open slide-out plays first (mirrors the cart's closeDialog). Escape always closes the
   * whole drawer (standard modal behaviour) — the in-panel Back button still steps back through
   * submenus.
   * @param {Event} event
   */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @returns {boolean} Whether the main menu drawer (the dialog) is open. */
  get isOpen() {
    return this.refs.details.open;
  }

  /**
   * The open target for an event: the closest submenu <details>, else the dialog container.
   * @param {Event | undefined} event
   * @returns {HTMLElement}
   */
  #getOpenTarget(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * The element carrying aria-expanded / receiving focus for `el`: the hamburger button for the
   * container, the <summary> for a submenu.
   * @param {HTMLElement} el
   * @returns {HTMLElement | null}
   */
  #triggerFor(el) {
    return el === this.refs.details ? this.refs.trigger : el.querySelector('summary');
  }

  /**
   * Toggle the drawer.
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the drawer (the dialog, modal) or a submenu (<details>).
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const el = this.#getOpenTarget(event);
    const isContainer = el === this.refs.details;

    if (isContainer) {
      // Only one drawer open at a time: close the cart when opening the menu.
      const cart = /** @type {(HTMLElement & { close?: () => void }) | null} */ (
        document.querySelector('cart-drawer-component')
      );
      if (typeof cart?.close === 'function') cart.close();
    }

    this.#triggerFor(el)?.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(el);

    // The container opens MODAL — native focus trap + inert background + ::backdrop. Submenus are
    // <details>. Then commit the hidden initial state with a forced reflow before adding
    // .menu-open, which drives the clip-path / translateY reveal transition (a forced reflow,
    // not requestAnimationFrame, which is paused when the tab is backgrounded).
    if (isContainer) {
      if (!(/** @type {HTMLDialogElement} */ (el).open)) /** @type {HTMLDialogElement} */ (el).showModal();
    } else {
      /** @type {HTMLDetailsElement} */ (el).open = true;
    }

    const panel = isContainer ? this.refs.menuDrawer : el.querySelector('.menu-drawer__submenu');
    void (panel instanceof HTMLElement ? panel.offsetHeight : el.offsetHeight);

    el.classList.add('menu-open');

    if (target) {
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
    }

    // Wait for the reveal to finish, then move focus to the panel's first focusable (the modal
    // already contains focus; this just lands it on the close button rather than the dialog).
    const drawer = el.querySelector('.menu-drawer, .menu-drawer__submenu');
    onAnimationEnd(drawer || el, () => this.#focusFirst(el), { subtree: false });
  }

  /**
   * Move focus to the first focusable link/button within a container.
   * @param {HTMLElement} container
   */
  #focusFirst(container) {
    const focusable = container.querySelector(FOCUSABLE_SELECTOR);
    if (focusable instanceof HTMLElement) focusable.focus();
  }

  /**
   * Go back (close the current submenu) or close the drawer.
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getOpenTarget(event));
  }

  /** Close the drawer. */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the drawer (dialog) or a submenu (<details>). The panel stays in layout while
   * .menu-open animates out, then the container closes natively (dialog.close()) or the submenu
   * <details> is reset — so the close mirrors the open (same easing/duration).
   * @param {HTMLElement} el
   */
  #close(el) {
    const isContainer = el === this.refs.details;
    this.#triggerFor(el)?.setAttribute('aria-expanded', 'false');

    el.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer transition, not the whole subtree (avoids child accordion /
    // resource-card animations, a Firefox issue).
    const drawer = el.querySelector('.menu-drawer, .menu-drawer__submenu');
    onAnimationEnd(
      drawer || el,
      () => {
        if (isContainer) {
          if (/** @type {HTMLDialogElement} */ (el).open) /** @type {HTMLDialogElement} */ (el).close();
          // The native modal restores focus to the opener; re-assert on the trigger to be safe.
          this.refs.trigger?.focus();
          // Collapse any submenus left open so the drawer reopens at the top level.
          this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(resetDetails);
        } else {
          resetDetails(/** @type {HTMLDetailsElement} */ (el));
          // Closing a submenu: move focus back into the main drawer panel.
          this.#focusFirst(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend listeners to remove will-change after animation (drops the stacking
   * context so submenus position correctly).
   */
  #setupAnimatedElementListeners() {
    this.querySelectorAll('.menu-drawer__animated-element').forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disable accordion animations so they don't transition when the drawer opens.
   * @param {HTMLElement} details - The container/submenu holding the accordions.
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');
    content.forEach((element) => {
      if (element instanceof HTMLElement) element.classList.add('details-content--no-animation');
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) element.classList.remove('details-content--no-animation');
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open <details> submenu to its closed state.
 * @param {HTMLDetailsElement | Element} element
 */
function resetDetails(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
